// Typed API clients for the two portals, generated from docs/openapi.{portal,admin}.json (`pnpm gen:api`).
// Session cookie auth (never API keys); every state-changing request carries the CSRF token (§11A.1).
import createClient, { type Middleware } from "openapi-fetch";
import type { paths as AdminPaths } from "./admin.gen";
import type { paths as PortalPaths } from "./portal.gen";

export * from "./types";
export type { AdminPaths, PortalPaths };

/** RFC 7807 problem returned by the connector. */
export class ApiError extends Error {
  constructor(public status: number, public code: string, detail: string, public body: unknown) {
    super(detail || code);
  }
}

const CSRF_KEY = "aawaz.csrf";

export function setCsrf(token: string | null): void {
  if (token) sessionStorage.setItem(CSRF_KEY, token);
  else sessionStorage.removeItem(CSRF_KEY);
}

const SAFE = new Set(["GET", "HEAD", "OPTIONS"]);

const middleware: Middleware = {
  async onRequest({ request }) {
    const csrf = sessionStorage.getItem(CSRF_KEY);
    if (csrf && !SAFE.has(request.method)) request.headers.set("x-csrf-token", csrf);
    return request;
  },
  async onResponse({ response }) {
    if (response.ok || response.status === 302) return response;
    let body: Record<string, unknown> = {};
    try {
      body = (await response.clone().json()) as Record<string, unknown>;
    } catch {
      /* non-JSON error */
    }
    throw new ApiError(response.status, String(body.code ?? "HTTP_ERROR"), String(body.detail ?? response.statusText), body);
  },
};

export const portal = createClient<PortalPaths>({ baseUrl: "", credentials: "include" });
portal.use(middleware);

export const admin = createClient<AdminPaths>({ baseUrl: "", credentials: "include" });
admin.use(middleware);

/** Unwrap an openapi-fetch result (errors are thrown by the middleware). */
export async function data<T>(p: Promise<{ data?: unknown }>): Promise<T> {
  const r = await p;
  return r.data as T;
}
